import { HStack, useDisclosure } from '@chakra-ui/react';
import { Typography } from 'Tokens';
import {
  GetAssessmentCurrencyConversionQuery_,
  GetRequiredMaterialMetricsQuery_,
  QuestionType_Enum_,
  useGetPoliciesDrQuery,
} from 'models';
import { AggregatedQualitativeAnswers, hasQuarterlyFrequency, MetricsTableData } from '..';
import { Button, Tooltip } from 'Atoms';
import { AIIcon } from 'Tokens/Icons/Function';
import { MetricsAIModal } from './MetricsAIModal';
import { SelectedMetric } from 'containers/Esrs/EsrsAssessment.hooks';
import { TimePeriods, TimePeriodsEnums } from '../../Requirement';
import { useTranslation } from 'utils/translation';
import { useFeatureFlags, useUserSetting } from 'containers/Navigation';
import { useCurrentCompany } from 'utils/hooks';
import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { MetricsAIDisclaimer } from './MetricsAIDisclaimer';
import { AssessableMetrics } from '../Metrics';
import { TRACKING_EVENTS } from 'utils/mixpanel';
import mixpanel from 'mixpanel-browser';

export const MetricsAISection = ({
  materialStandardId,
  reportingUnitId,
  disclosureRequirementRef,
  allFilteredMetrics,
  selectedQuarter,
  selectedMetric,
  setSelectedMetric,
  onOpen,
  isGroup,
  rowData,
  setRowData,
  qualitativeMetrics,
  summaryQualitativeMetrics,
  isPolicy = false,
  aggregatedMetricsAnswers,
  parentStandardId,
  requiredMaterialMetrics,
  currencyConversionData,
  areMetricsYearly,
}: {
  materialStandardId: string;
  disclosureRequirementRef: string;
  reportingUnitId?: string;
  allFilteredMetrics: MetricsTableData[];
  selectedQuarter: TimePeriods;
  selectedMetric?: SelectedMetric;
  setSelectedMetric: (param: SelectedMetric) => void;
  onOpen: () => void;
  isGroup: boolean;
  rowData?: MetricsTableData;
  setRowData: (data: MetricsTableData | undefined) => void;
  qualitativeMetrics: MetricsTableData[];
  summaryQualitativeMetrics: AssessableMetrics;
  isPolicy?: boolean;
  aggregatedMetricsAnswers: AggregatedQualitativeAnswers;
  parentStandardId: string | undefined;
  requiredMaterialMetrics: GetRequiredMaterialMetricsQuery_['esrs_MaterialMetric'];
  currencyConversionData?: GetAssessmentCurrencyConversionQuery_['esrs_AssessmentCurrencyConversion'];
  areMetricsYearly: boolean;
}) => {
  const { isOpen: isAIModalOpen, onClose: onAIModalClose, onOpen: onAIModalOpen } = useDisclosure();
  const [isFromIcon, setIsFromIcon] = useState(false);
  const {
    isOpen: isAIDisclaimerOpen,
    onClose: onAIDisclaimerClose,
    onOpen: onAIDisclaimerOpen,
  } = useDisclosure();
  const [aiDoNotShowAgain, setAiDoNotShowAgain] = useUserSetting('ai-do-not-show-again', false);
  const [aiDisclaimerConfirmedAt] = useUserSetting<string | null>(
    'ai-disclaimer-confirmed-at',
    null
  );
  const showAiDisclaimerModal = aiDisclaimerConfirmedAt === null || aiDoNotShowAgain === false;
  const { t } = useTranslation(['common, ai']);
  const { company } = useCurrentCompany();
  const { hasAiAccess } = useFeatureFlags({ company });
  const { esrsAssessmentId = '', standardRef = '' } = useParams();

  // Policy attachments, used here to track and disable AI button for policies and passed as prop to the modal
  const { data: policiesData } = useGetPoliciesDrQuery({
    variables: {
      reference: disclosureRequirementRef,
      esrsAssessmentId,
      standardRef,
    },
    skip: !isPolicy || !disclosureRequirementRef || !esrsAssessmentId || !standardRef,
  });

  const policyAttachments = useMemo(() => {
    return policiesData?.DisclosureRequirement_by_pk?.policies[0]?.attachmentBox?.attachments ?? [];
  }, [policiesData]);

  const isYearSelected = selectedQuarter === TimePeriodsEnums.year;
  const noOverviewMetrics =
    !areMetricsYearly &&
    isYearSelected &&
    !qualitativeMetrics.length &&
    !summaryQualitativeMetrics.length;

  const quantitativeMetrics = useMemo(
    () =>
      allFilteredMetrics.filter((metric) => {
        const isQuantitative = metric.metric.metricType === QuestionType_Enum_.Decimal_;
        // Exclude quarterly collected metrics in year overview
        const shouldExclude = isYearSelected && hasQuarterlyFrequency(metric.metric);

        return isQuantitative && !shouldExclude;
      }),
    [allFilteredMetrics, isYearSelected]
  );

  const tooltipLabel = useMemo(() => {
    if (!hasAiAccess) return t('common:aiConsent');
    if (isPolicy && !policyAttachments.length) return 'Please add a policy to unlock AI';
    if (noOverviewMetrics) return 'All metrics are collected quarterly';
    return '';
  }, [hasAiAccess, isPolicy, policyAttachments.length, noOverviewMetrics]);

  const isAIDisabled = useMemo(() => {
    return !hasAiAccess || (isPolicy && !policyAttachments.length) || noOverviewMetrics;
  }, [hasAiAccess, isPolicy, policyAttachments.length, noOverviewMetrics]);

  return (
    <>
      <HStack
        w="100%"
        p="4px 4px 4px 8px"
        border="1px solid"
        borderColor="border.selected.accent"
        borderRadius="6px"
      >
        <HStack spacing="8px" w="100%" justifyContent="space-between">
          <HStack spacing="8px">
            <AIIcon color="text.info" />
            <Typography variant="bodyStrong">
              {isPolicy
                ? 'These answers could be generated by AI using your policy documents'
                : 'Generate answers with AI'}
            </Typography>
          </HStack>

          <Tooltip label={tooltipLabel} isDisabled={!isAIDisabled} placement="bottom-end">
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                mixpanel.track(TRACKING_EVENTS.ESRS.AI.START);
                if (showAiDisclaimerModal) {
                  onAIDisclaimerOpen();
                  setIsFromIcon(false);
                } else {
                  onAIModalOpen();
                }
              }}
              isDisabled={isAIDisabled}
            >
              Start
            </Button>
          </Tooltip>
        </HStack>
      </HStack>

      <MetricsAIDisclaimer
        isOpen={isAIDisclaimerOpen}
        onClose={onAIDisclaimerClose}
        onAIModalOpen={onAIModalOpen}
        aiDisclaimerConfirmedAt={aiDisclaimerConfirmedAt}
        aiDoNotShowAgain={aiDoNotShowAgain}
        setAiDoNotShowAgain={setAiDoNotShowAgain}
        isFromIcon={isFromIcon}
      />

      <MetricsAIModal
        isOpen={isAIModalOpen}
        onAIDisclaimerOpen={onAIDisclaimerOpen}
        onClose={onAIModalClose}
        disclosureRequirementRef={disclosureRequirementRef}
        materialStandardId={materialStandardId}
        reportingUnitId={reportingUnitId}
        allFilteredMetrics={quantitativeMetrics}
        selectedQuarter={selectedQuarter}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        onOpen={onOpen}
        isGroup={isGroup}
        rowData={rowData}
        setRowData={setRowData}
        qualitativeMetrics={isYearSelected ? qualitativeMetrics : []}
        summaryQualitativeMetrics={isYearSelected ? summaryQualitativeMetrics : []}
        aggregatedMetricsAnswers={aggregatedMetricsAnswers}
        isPolicy={isPolicy}
        policyAttachments={policyAttachments}
        parentStandardId={parentStandardId}
        requiredMaterialMetrics={requiredMaterialMetrics}
        currencyConversionData={currencyConversionData}
        setIsFromIcon={setIsFromIcon}
      />
    </>
  );
};
